import config from '@/config'
import { isGokollab, isGokollabMobile } from '@ghl/clientportal-core/init'
import type { LocationQuery } from 'vue-router'

function baseUrl(): string {
  if (isGokollab() && import.meta.env.DEV) {
    return 'http://localhost:3032'
  }
  return window.location.origin
}

export function currentUrl(): string {
  return window.location.href
}

export function externalPathRedirect(
  path?: string,
  query?: string,
  newTab?: boolean
): void {
  if (newTab) {
    window.open(
      `${baseUrl()}/${path ? path : ''}?${query ? query : ''}`,
      '_blank'
    )
  } else {
    window.location.href = `${baseUrl()}/${path ? path : ''}?${
      query ? query : ''
    }`
  }
}

export function getGokollabDomain() {
  return isGokollabMobile()
    ? config.GOKOLLAB_MOBILE_DOMAIN
    : config.GOKOLLAB_DOMAIN
}

const LOGIN_PATH = '/login'
const ROOT_PATH = '/'

/** Same-origin only; blocks `/`, `/login`, and other origins. */
export function isSafeRedirect(url: string): boolean {
  const trimmed = url.trim()
  if (!trimmed) {
    return false
  }

  try {
    if (typeof window === 'undefined') {
      return false
    }

    const resolved = new URL(trimmed, window.location.origin)
    if (resolved.origin !== window.location.origin) {
      return false
    }

    const path = resolved.pathname
    return (
      path !== ROOT_PATH &&
      path !== LOGIN_PATH &&
      !path.startsWith(`${LOGIN_PATH}/`)
    )
  } catch {
    return false
  }
}

export function buildLoginRedirectQuery(
  fullPath: string
): Record<string, string> {
  if (!isSafeRedirect(fullPath)) {
    return {}
  }

  return { redirectUrl: fullPath }
}

export function getRedirectTargetFromQuery(
  query: LocationQuery
): string | null {
  const raw = query.redirectUrl
  if (raw == null || raw === '') {
    return null
  }
  const value = Array.isArray(raw) ? raw[0] : raw
  if (typeof value !== 'string' || !value.trim()) {
    return null
  }
  return isSafeRedirect(value) ? value : null
}

/** Marks in-app bounces that must keep `redirectUrl` (e.g. private-group gate). */
export const INTERNAL_REDIRECT_RESOURCE_KEY = 'resource'
export const INTERNAL_REDIRECT_RESOURCE_VALUE = 'private'

export function isInternalRedirectQuery(query: LocationQuery): boolean {
  const raw = query[INTERNAL_REDIRECT_RESOURCE_KEY]
  const value = Array.isArray(raw) ? raw[0] : raw
  return value === INTERNAL_REDIRECT_RESOURCE_VALUE
}

export function withInternalRedirectQuery(
  query: LocationQuery = {}
): LocationQuery {
  return {
    ...query,
    [INTERNAL_REDIRECT_RESOURCE_KEY]: INTERNAL_REDIRECT_RESOURCE_VALUE,
  }
}

export function queryWithoutLoginRedirect(query: LocationQuery): LocationQuery {
  const {
    redirectUrl: _redirectUrl,
    [INTERNAL_REDIRECT_RESOURCE_KEY]: _resource,
    ...rest
  } = query
  return rest
}

/** Returns `path` with query serialized after stripping `redirectUrl` and `resource`. */
export function stripLoginRedirectParams(
  path: string,
  query: LocationQuery
): string {
  const rest = queryWithoutLoginRedirect(query)
  const search = new URLSearchParams()
  for (const [key, val] of Object.entries(rest)) {
    if (val === undefined || val === null) {
      continue
    }
    const values = Array.isArray(val) ? val : [val]
    for (const v of values) {
      if (typeof v === 'string') {
        search.append(key, v)
      }
    }
  }
  const qs = search.toString()
  return qs ? `${path}?${qs}` : path
}

